(function(){

'use strict';

/* ============================================================
   COREVIAN UNIVERSITY — CONTROLLED DOCUMENTS MODULE V2
   
   Changes from V1:
   - ACK_STATEMENT constant — identical text in UI and RPC call
   - assignmentMap() now also indexes by string(id) to prevent
     UUID type mismatches
   - detailHtml() acknowledgment block shows for ALL document types
     (policies, SOPs, BBP) whether opened from Required Reading
     or Current Documents tab
   - Optimistic UI on acknowledgment — DOM updates instantly,
     database writes in background
   - Qualification Passport tab added — shows all six evidence
     types independently per the Corevian qualification framework
   - Debounce on renderControlledDocuments nav button
   - Graceful degradation if roleAssignments query fails
   ============================================================ */

const DOC_VIEW = 'documents';

/* ── SINGLE SOURCE OF TRUTH for acknowledgment statement ──────
   Used verbatim in the checkbox label AND sent to the RPC.
   The employee reads exactly what gets recorded.
   ─────────────────────────────────────────────────────────── */
const ACK_STATEMENT =
  'I acknowledge that I have reviewed this assigned controlled document ' +
  'and understand that I am responsible for following the current approved ' +
  'version applicable to my role and assigned work. I understand that this ' +
  'acknowledgment confirms receipt and review of the document and does not, ' +
  'by itself, establish training completion, competency, field readiness, ' +
  'or operational authorization.';

let docState = {
  profile:          null,
  user:             null,
  assignments:      [],
  versions:         [],
  documents:        [],
  acknowledgments:  [],
  roleAssignments:  [],
  activeTab:        'required',
  selectedVersionId: null,
  loading:          false
};

/* ── UTILITIES ─────────────────────────────────────────────── */

function cvnEsc(value){
  if(value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g,'&amp;')
    .replace(/</g,'&lt;')
    .replace(/>/g,'&gt;')
    .replace(/"/g,'&quot;')
    .replace(/'/g,'&#039;');
}

function cvnDate(value, includeTime){
  if(!value) return 'Not set';
  const raw = String(value).trim();
  const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if(dateOnlyMatch){
    const d = new Date(
      Number(dateOnlyMatch[1]),
      Number(dateOnlyMatch[2]) - 1,
      Number(dateOnlyMatch[3])
    );
    if(Number.isNaN(d.getTime())) return cvnEsc(value);
    return d.toLocaleDateString(undefined,{year:'numeric',month:'short',day:'numeric'});
  }
  const d = new Date(value);
  if(Number.isNaN(d.getTime())) return cvnEsc(value);
  return includeTime
    ? d.toLocaleString()
    : d.toLocaleDateString(undefined,{year:'numeric',month:'short',day:'numeric'});
}

function isDemoVisible(){
  const banner = document.getElementById('demoBanner');
  if(!banner) return false;
  return banner.style.display !== 'none' &&
    getComputedStyle(banner).display !== 'none';
}

function getClient(){
  if(typeof window.getSupabaseClient === 'function') return window.getSupabaseClient();
  if(typeof getSupabaseClient === 'function') return getSupabaseClient();
  return null;
}

function debounce(fn, ms){
  let t;
  return function(){ clearTimeout(t); t = setTimeout(fn, ms); };
}

/* ── DATA MAPS ─────────────────────────────────────────────── */

function docMap(){
  return new Map(docState.documents.map(d => [d.id, d]));
}

function versionMap(){
  return new Map(docState.versions.map(v => [v.id, v]));
}

function ackMap(){
  /* Key by both raw id and string(id) to survive UUID type differences */
  const m = new Map();
  docState.acknowledgments.forEach(a => {
    m.set(a.document_version_id, a);
    m.set(String(a.document_version_id), a);
  });
  return m;
}

function assignmentMap(){
  /* Key by both raw id and string(id) — prevents UUID vs string mismatch */
  const m = new Map();
  docState.assignments.forEach(a => {
    m.set(a.document_version_id, a);
    m.set(String(a.document_version_id), a);
  });
  return m;
}

function effectiveAssignmentStatus(a){
  if(!a) return 'unassigned';
  if(a.status === 'completed' || a.completed_at) return 'completed';
  if(a.status === 'waived') return 'waived';
  if(a.due_at && new Date(a.due_at).getTime() < Date.now()) return 'overdue';
  return a.status || 'assigned';
}

function statusClass(status){
  if(status === 'completed' || status === 'acknowledged') return 'complete';
  if(status === 'overdue') return 'overdue';
  return 'pending';
}

function documentLabel(v){
  const d = docMap().get(v.document_id);
  return {
    code:        d ? d.document_code  : 'CONTROLLED DOCUMENT',
    title:       d ? d.title          : 'Controlled Document',
    description: d ? d.description    : '',
    type:        d ? d.document_type  : ''
  };
}

/* ── DATA LOADING ──────────────────────────────────────────── */

async function loadIdentity(){
  const client = getClient();
  if(!client) throw new Error('Supabase client is unavailable.');
  const auth = await client.auth.getUser();
  if(auth.error) throw auth.error;
  const user = auth.data && auth.data.user;
  if(!user) throw new Error('Authenticated Corevian University user could not be identified.');
  const profileRes = await client
    .from('profiles')
    .select('user_id,employee_id,full_name,role,job_title,active')
    .eq('user_id', user.id)
    .single();
  if(profileRes.error) throw profileRes.error;
  if(!profileRes.data || profileRes.data.active !== true){
    throw new Error('This Corevian University profile is inactive.');
  }
  docState.user    = user;
  docState.profile = profileRes.data;
}

async function loadDocumentData(){
  const client = getClient();
  if(!client) throw new Error('Supabase client is unavailable.');
  if(!docState.user) await loadIdentity();
  const uid = docState.user.id;

  /* Run all five queries in parallel. roleAssignments failure is
     non-fatal — we degrade gracefully rather than blocking the view. */
  const [assignRes, ackRes, versionRes, docRes, roleRes] = await Promise.all([
    client.from('document_assignments')
      .select('id,user_id,document_version_id,assigned_at,due_at,status,assigned_by,completed_at')
      .eq('user_id', uid)
      .order('assigned_at',{ascending:false}),
    client.from('document_acknowledgments')
      .select('id,assignment_id,user_id,document_version_id,acknowledged_at,acknowledgment_statement')
      .eq('user_id', uid)
      .order('acknowledged_at',{ascending:false}),
    client.from('controlled_document_versions')
      .select('id,document_id,version_number,status,effective_date,review_due_date,change_summary,content_text,file_reference,acknowledgment_required,training_required,competency_required,approved_by,approved_at,created_at')
      .eq('status','approved')
      .order('effective_date',{ascending:false}),
    client.from('controlled_documents')
      .select('id,document_code,title,document_type,department,document_owner,description,active,created_at,updated_at')
      .eq('active',true)
      .order('document_code',{ascending:true}),
    client.from('controlled_document_role_assignments')
      .select('id,document_version_id,role,required,created_at')
  ]);

  for(const r of [assignRes, ackRes, versionRes, docRes]){
    if(r.error) throw r.error;
  }
  /* roleAssignments is non-fatal */
  if(roleRes.error){
    console.warn('[Corevian Docs] Role assignments unavailable — continuing without role filter:', roleRes.error.message);
  }

  docState.assignments    = assignRes.data  || [];
  docState.acknowledgments= ackRes.data     || [];
  docState.versions       = versionRes.data || [];
  docState.documents      = docRes.data     || [];
  docState.roleAssignments= roleRes.error ? [] : (roleRes.data || []);
}

async function loadLeadershipReadiness(){
  if(!docState.profile || !['leadership','admin'].includes(docState.profile.role)){
    return {assignments:[], acks:[], profiles:[]};
  }
  const client = getClient();
  const [assignRes, ackRes, profRes] = await Promise.all([
    client.from('document_assignments')
      .select('id,user_id,document_version_id,assigned_at,due_at,status,completed_at')
      .order('assigned_at',{ascending:false}),
    client.from('document_acknowledgments')
      .select('id,user_id,document_version_id,acknowledged_at')
      .order('acknowledged_at',{ascending:false}),
    client.from('profiles')
      .select('user_id,employee_id,full_name,role,job_title,active')
      .eq('active',true)
      .order('full_name',{ascending:true})
  ]);
  for(const r of [assignRes, ackRes, profRes]) if(r.error) throw r.error;
  return {
    assignments: assignRes.data || [],
    acks:        ackRes.data    || [],
    profiles:    profRes.data   || []
  };
}

/* ── NAVIGATION INJECTION ──────────────────────────────────── */

function injectNav(){
  if(document.getElementById('cvnDocsNavBlock')) return;
  const exam = document.getElementById('navExam');
  if(!exam) return;
  const block = document.createElement('div');
  block.id = 'cvnDocsNavBlock';
  block.className = 'cvn-docs-nav-block';
  block.innerHTML = `
    <div class="nav-divider"></div>
    <div class="sidebar-section">Knowledge & Document Control</div>
    <button class="nav-btn" id="navDocuments" type="button">
      <div class="num">P</div>
      <div class="nav-text">
        <span class="nav-title">Policies & SOPs</span>
        <span class="nav-sub">Required reading & controlled documents</span>
      </div>
    </button>
    <button class="nav-btn" id="navPassport" type="button">
      <div class="num">Q</div>
      <div class="nav-text">
        <span class="nav-title">Qualification Passport</span>
        <span class="nav-sub">Evidence trail & authorization status</span>
      </div>
    </button>`;
  exam.insertAdjacentElement('afterend', block);

  const docsBtn = document.getElementById('navDocuments');
  if(docsBtn){
    docsBtn.addEventListener('click', debounce(function(){
      if(isDemoVisible()) return;
      if(typeof window.showView === 'function') window.showView(DOC_VIEW, docsBtn);
      else renderControlledDocuments();
    }, 200));
  }

  const passportBtn = document.getElementById('navPassport');
  if(passportBtn){
    passportBtn.addEventListener('click', debounce(function(){
      if(isDemoVisible()) return;
      if(typeof window.showView === 'function') window.showView('passport', passportBtn);
      else renderQualificationPassport();
    }, 200));
  }

  syncDemoVisibility();
}

function syncDemoVisibility(){
  const block = document.getElementById('cvnDocsNavBlock');
  if(block) block.style.display = 'block';
}

function installDemoObserver(){
  const banner = document.getElementById('demoBanner');
  if(!banner || banner.dataset.docsObserverReady === 'true') return;
  banner.dataset.docsObserverReady = 'true';
  new MutationObserver(syncDemoVisibility).observe(banner,{attributes:true,attributeFilter:['style','class']});
}

function installViewHook(){
  if(window.__corevianDocsViewHookReady) return;
  if(typeof window.showView !== 'function') return;
  const original = window.showView;
  window.showView = function(view, el){
    try{
      if(!window.demoMode) sessionStorage.setItem('corevianCurrentView', view);
    }catch(err){
      console.warn('Unable to save current Corevian view:', err);
    }
    if(view === DOC_VIEW){
      document.querySelectorAll('#corevian-university-app .nav-btn').forEach(b=>b.classList.remove('active'));
      const nav = document.getElementById('navDocuments');
      if(nav) nav.classList.add('active');
      renderControlledDocuments();
      return;
    }
    if(view === 'passport'){
      document.querySelectorAll('#corevian-university-app .nav-btn').forEach(b=>b.classList.remove('active'));
      const nav = document.getElementById('navPassport');
      if(nav) nav.classList.add('active');
      renderQualificationPassport();
      return;
    }
    return original.apply(this, arguments);
  };
  window.__corevianDocsViewHookReady = true;
}

/* ── LOADING / ERROR STATES ────────────────────────────────── */

function renderLoading(){
  const root = document.getElementById('contentInner');
  if(root) root.innerHTML = `
    <div class="cvn-docs-shell">
      <div class="cvn-doc-notice">Loading controlled documents and required-reading status…</div>
    </div>`;
}

function renderError(err){
  const root = document.getElementById('contentInner');
  if(root) root.innerHTML = `
    <div class="cvn-docs-shell">
      <div class="cvn-doc-error">${cvnEsc(err && err.message ? err.message : 'Unable to load the controlled document library.')}</div>
    </div>`;
}

/* ── STATS BAR ─────────────────────────────────────────────── */

function renderStats(){
  const acks     = ackMap();
  const required = docState.assignments.filter(a => {
    const s = effectiveAssignmentStatus(a);
    return s !== 'completed' && s !== 'waived' && !acks.has(String(a.document_version_id));
  }).length;
  const completed = docState.assignments.filter(a =>
    effectiveAssignmentStatus(a) === 'completed' ||
    acks.has(String(a.document_version_id))
  ).length;
  const overdue = docState.assignments.filter(a =>
    effectiveAssignmentStatus(a) === 'overdue'
  ).length;
  const approved = new Set(docState.versions.map(v=>v.document_id).filter(Boolean)).size;
  return `
    <div class="cvn-docs-stats">
      <div class="cvn-doc-stat"><div class="cvn-doc-stat-value">${required}</div><div class="cvn-doc-stat-label">Assigned</div></div>
      <div class="cvn-doc-stat"><div class="cvn-doc-stat-value">${completed}</div><div class="cvn-doc-stat-label">Completed</div></div>
      <div class="cvn-doc-stat"><div class="cvn-doc-stat-value">${overdue}</div><div class="cvn-doc-stat-label">Overdue</div></div>
      <div class="cvn-doc-stat"><div class="cvn-doc-stat-value">${approved}</div><div class="cvn-doc-stat-label">Approved Documents</div></div>
    </div>`;
}

/* ── TABS ──────────────────────────────────────────────────── */

function tabsHtml(){
  const leadership = docState.profile && ['leadership','admin'].includes(docState.profile.role);
  return `
    <div class="cvn-doc-tabs" role="tablist" aria-label="Controlled document views">
      <button class="cvn-doc-tab ${docState.activeTab==='required'?'active':''}" data-doc-tab="required">Required Reading</button>
      <button class="cvn-doc-tab ${docState.activeTab==='library'?'active':''}" data-doc-tab="library">Current Documents</button>
      <button class="cvn-doc-tab ${docState.activeTab==='history'?'active':''}" data-doc-tab="history">My Acknowledgments</button>
      ${leadership?`<button class="cvn-doc-tab ${docState.activeTab==='readiness'?'active':''}" data-doc-tab="readiness">Document Readiness</button>`:''}
    </div>`;
}

function flags(v){
  const out = [];
  if(v.acknowledgment_required) out.push('Acknowledgment Required');
  if(v.training_required)       out.push('Training Required');
  if(v.competency_required)     out.push('Competency Required');
  if(!out.length)               out.push('Reference Only');
  return `<div class="cvn-doc-flags">${out.map(x=>`<span class="cvn-doc-flag">${cvnEsc(x)}</span>`).join('')}</div>`;
}

/* ── REQUIRED READING ──────────────────────────────────────── */

function requiredHtml(){
  const versions = versionMap();
  const docs     = docMap();
  const acks     = ackMap();
  const outstanding = docState.assignments.filter(a => {
    const s = effectiveAssignmentStatus(a);
    return s !== 'completed' && s !== 'waived' && !acks.has(String(a.document_version_id));
  });
  if(!outstanding.length){
    return `<div class="cvn-doc-empty">No controlled-document assignments are currently outstanding for your account.</div>`;
  }
  const records = outstanding.map(a => {
    const v = versions.get(a.document_version_id) || versions.get(String(a.document_version_id));
    if(!v) return null;
    const d = docs.get(v.document_id);
    if(!d) return null;
    const ack = acks.get(v.id) || acks.get(String(v.id));
    let status = effectiveAssignmentStatus(a);
    if(ack && status !== 'completed') status = 'acknowledged';
    const code = String(d.document_code||'').toUpperCase();
    let category = 'other';
    if(code.startsWith('CHL-POL-'))       category = 'policies';
    else if(code.startsWith('CHL-SOP-')) category = 'sops';
    else if(code.startsWith('CHL-BBP-')||code.startsWith('CHL-ECP-')) category = 'bbp';
    return { assignment:a, version:v, document:d, ack, status, code, category };
  }).filter(Boolean);

  const numericSort = (a,b) => a.code.localeCompare(b.code,undefined,{numeric:true,sensitivity:'base'});
  records.sort(numericSort);

  const policies = records.filter(r=>r.category==='policies');
  const sops     = records.filter(r=>r.category==='sops');
  const bbp      = records.filter(r=>r.category==='bbp');
  const other    = records.filter(r=>r.category==='other');
  const pol015   = records.find(r=>r.code==='CHL-POL-015');
  const bbpProgramRecords = [
    ...(pol015?[pol015]:[]),
    ...bbp
  ].sort((a,b)=>{
    const order = c=>{
      if(c==='CHL-POL-015') return 1;
      if(c==='CHL-ECP-001') return 2;
      if(c.startsWith('CHL-BBP-')) return 3;
      return 4;
    };
    const oa=order(a.code), ob=order(b.code);
    return oa!==ob ? oa-ob : numericSort(a,b);
  });

  const needsActionCount = records.length;
  const overdueCount = records.filter(r=>String(r.status).toLowerCase()==='overdue').length;

  const documentRow = r => {
    const {assignment:a, version:v, document:d, ack, status} = r;
    return `
      <div class="cvn-library-doc-row"
        data-document-status="${cvnEsc(status)}"
        data-doc-search="${cvnEsc(`${r.code} ${d.title||''} ${d.description||''}`)}">
        <div class="cvn-library-doc-main">
          <div class="cvn-library-doc-code">${cvnEsc(r.code||'CONTROLLED DOCUMENT')} <span>· VERSION ${cvnEsc(v.version_number)}</span></div>
          <div class="cvn-library-doc-title">${cvnEsc(d.title||'Controlled Document')}</div>
          <div class="cvn-library-doc-meta">
            Assigned ${cvnDate(a.assigned_at)} &nbsp;·&nbsp;
            Due ${cvnDate(a.due_at)} &nbsp;·&nbsp;
            Effective ${cvnDate(v.effective_date)}
          </div>
          <div class="cvn-library-doc-flags">${flags(v)}</div>
        </div>
        <div class="cvn-library-doc-side">
          <span class="cvn-doc-status ${statusClass(status)}">${cvnEsc(String(status||'assigned').replaceAll('_',' '))}</span>
          <button class="cvn-doc-btn cvn-library-read-btn" type="button" data-open-version="${cvnEsc(v.id)}">Read Document</button>
          ${ack?`<div class="cvn-library-ack-note">Acknowledged ${cvnDate(ack.acknowledged_at)}</div>`:''}
        </div>
      </div>`;
  };

  const categoryCard = ({key,title,description,count,label}) => `
    <button type="button" class="cvn-library-category-card" data-doc-category="${cvnEsc(key)}">
      <div class="cvn-library-category-top">
        <span class="cvn-library-category-label">${cvnEsc(label)}</span>
        <span class="cvn-library-category-count">${count}</span>
      </div>
      <div class="cvn-library-category-title">${cvnEsc(title)}</div>
      <div class="cvn-library-category-desc">${cvnEsc(description)}</div>
      <div class="cvn-library-category-action">View Documents →</div>
    </button>`;

  const section = (key,eyebrow,title,description,rows) => `
    <section class="cvn-library-section" data-library-section="${cvnEsc(key)}" hidden>
      <div class="cvn-library-section-head">
        <button type="button" class="cvn-library-back" data-library-back>← All Required Reading</button>
        <div class="cvn-library-section-eyebrow">${cvnEsc(eyebrow)}</div>
        <div class="cvn-library-section-title">${cvnEsc(title)}</div>
        <div class="cvn-library-section-desc">${cvnEsc(description)}</div>
        <div class="cvn-library-section-count">${rows.length} ${rows.length===1?'document':'documents'} requiring attention</div>
      </div>
      <div class="cvn-library-section-docs">
        ${rows.length?rows.map(documentRow).join(''):`<div class="cvn-doc-empty">No outstanding documents in this category.</div>`}
      </div>
    </section>`;

  const bbpSection = () => {
    const foundation = bbpProgramRecords.filter(r=>r.code==='CHL-POL-015'||r.code==='CHL-ECP-001');
    const prevention = bbpProgramRecords.filter(r=>['CHL-BBP-001','CHL-BBP-002','CHL-BBP-003'].includes(r.code));
    const response   = bbpProgramRecords.filter(r=>['CHL-BBP-004','CHL-BBP-005','CHL-BBP-006'].includes(r.code));
    const oversight  = bbpProgramRecords.filter(r=>['CHL-BBP-007','CHL-BBP-008','CHL-BBP-009'].includes(r.code));
    const group = (title,description,rows) => {
      if(!rows.length) return '';
      return `<div class="cvn-bbp-group">
        <div class="cvn-bbp-group-head">
          <div class="cvn-bbp-group-title">${cvnEsc(title)}</div>
          <div class="cvn-bbp-group-desc">${cvnEsc(description)}</div>
        </div>
        <div class="cvn-bbp-group-docs">${rows.map(documentRow).join('')}</div>
      </div>`;
    };
    return `
      <section class="cvn-library-section" data-library-section="bbp" hidden>
        <div class="cvn-library-section-head">
          <button type="button" class="cvn-library-back" data-library-back>← All Required Reading</button>
          <div class="cvn-library-section-eyebrow">SAFETY & OCCUPATIONAL EXPOSURE</div>
          <div class="cvn-library-section-title">BBP & Occupational Exposure</div>
          <div class="cvn-library-section-desc">Bloodborne-pathogen prevention, exposure control, vaccination, response, training, and program oversight.</div>
          <div class="cvn-library-section-count">${bbpProgramRecords.length} program ${bbpProgramRecords.length===1?'document':'documents'} requiring attention</div>
        </div>
        ${group('Program Foundation','The governing policy and Exposure Control Plan.',foundation)}
        ${group('Exposure Determination & Prevention','Exposure classification and Hepatitis B prevention controls.',prevention)}
        ${group('Exposure Response','Incident documentation, medical referral, and provider handoff.',response)}
        ${group('Training & Program Oversight','Training evidence, annual program review, and sharps-injury records.',oversight)}
      </section>`;
  };

  return `
    <div class="cvn-required-library" id="cvnRequiredLibrary">
      <div class="cvn-library-toolbar">
        <div class="cvn-library-search-wrap">
          <input id="cvnRequiredSearch" class="cvn-library-search" type="search"
            placeholder="Search by document number, title, or keyword..."
            autocomplete="off" aria-label="Search required reading">
        </div>
        <div class="cvn-library-filter-row">
          <button type="button" class="cvn-library-filter active" data-required-filter="all">All</button>
          <button type="button" class="cvn-library-filter" data-required-filter="action">Needs Action</button>
          <button type="button" class="cvn-library-filter" data-required-filter="overdue">Overdue</button>
        </div>
      </div>
      <div class="cvn-library-home" data-library-home>
        <div class="cvn-library-home-head">
          <div>
            <div class="cvn-library-home-eyebrow">CONTROLLED DOCUMENT LIBRARY</div>
            <div class="cvn-library-home-title">Required Reading</div>
            <div class="cvn-library-home-desc">Select a document library to review the controlled material currently requiring your attention.</div>
          </div>
          <div class="cvn-library-home-summary">
            <strong>${needsActionCount}</strong><span>Need Action</span>
            <strong>${overdueCount}</strong><span>Overdue</span>
          </div>
        </div>
        <div class="cvn-library-category-grid">
          ${categoryCard({key:'policies',label:'GOVERNANCE',title:'Policies',description:'Company governance, workforce, safety, privacy, and compliance requirements.',count:policies.length})}
          ${categoryCard({key:'sops',label:'OPERATIONS',title:'Standard Operating Procedures',description:'Field procedures, route execution, safety response, and clinical logistics controls.',count:sops.length})}
          ${categoryCard({key:'bbp',label:'SAFETY & EXPOSURE',title:'BBP & Occupational Exposure',description:'Exposure-control policy, ECP, vaccination, incident response, training, and records.',count:bbpProgramRecords.length})}
          ${categoryCard({key:'other',label:'CONTROLLED KNOWLEDGE',title:'Other Controlled Documents',description:'Additional controlled plans, standards, references, and assigned documents.',count:other.length})}
        </div>
      </div>
      ${section('policies','GOVERNANCE & COMPLIANCE','Policies','Current Corevian policies requiring your review.',policies)}
      ${section('sops','OPERATIONAL EXECUTION','Standard Operating Procedures','Current Corevian procedures governing field and operational execution.',sops)}
      ${bbpSection()}
      ${section('other','CONTROLLED KNOWLEDGE','Other Controlled Documents','Additional controlled material assigned to your account.',other)}
      <div class="cvn-doc-empty" id="cvnRequiredSearchEmpty" hidden>No required-reading documents match your search.</div>
    </div>`;
}

/* ── CURRENT DOCUMENTS LIBRARY ─────────────────────────────── */

function libraryHtml(){
  if(!docState.versions.length){
    return `<div class="cvn-doc-empty">No approved controlled documents are currently visible for your role or assignment.</div>`;
  }
  const currentVersionByDocument = new Map();
  docState.versions.forEach(v=>{
    if(!v.document_id) return;
    const existing = currentVersionByDocument.get(v.document_id);
    if(!existing){ currentVersionByDocument.set(v.document_id,v); return; }
    if(String(v.version_number||'').localeCompare(String(existing.version_number||''),undefined,{numeric:true,sensitivity:'base'})>0){
      currentVersionByDocument.set(v.document_id,v);
    }
  });
  const currentVersions = Array.from(currentVersionByDocument.values());
  const records = currentVersions.map(v=>{
    const d = documentLabel(v);
    if(!d) return null;
    const roles = docState.roleAssignments.filter(r=>r.document_version_id===v.id||String(r.document_version_id)===String(v.id)).map(r=>r.role);
    const code = String(d.code||'').toUpperCase();
    let category = 'other';
    if(code.startsWith('CHL-POL-'))       category = code==='CHL-POL-015'?'bbp':'policies';
    else if(code.startsWith('CHL-SOP-')) category = 'sops';
    else if(code.startsWith('CHL-BBP-')||code.startsWith('CHL-ECP-')) category = 'bbp';
    return {v,d,roles,code,category};
  }).filter(Boolean);
  records.sort((a,b)=>a.code.localeCompare(b.code,undefined,{numeric:true,sensitivity:'base'}));
  const groups = {
    policies: records.filter(r=>r.category==='policies'),
    sops:     records.filter(r=>r.category==='sops'),
    bbp:      records.filter(r=>r.category==='bbp'),
    other:    records.filter(r=>r.category==='other')
  };
  const categoryCard = ({key,label,title,description,count}) => `
    <button type="button" class="cvn-library-category-card" data-current-library-category="${cvnEsc(key)}">
      <div class="cvn-library-category-top">
        <span class="cvn-library-category-label">${cvnEsc(label)}</span>
        <span class="cvn-library-category-count">${count}</span>
      </div>
      <div class="cvn-library-category-title">${cvnEsc(title)}</div>
      <div class="cvn-library-category-desc">${cvnEsc(description)}</div>
      <div class="cvn-library-category-action">View Documents →</div>
    </button>`;
  return `
    <div class="cvn-required-library" data-current-library>
      <div class="cvn-library-home">
        <div class="cvn-library-heading">
          <div>
            <div class="cvn-library-eyebrow">CONTROLLED DOCUMENT LIBRARY</div>
            <h2 class="cvn-library-title">Current Documents</h2>
            <div class="cvn-library-subtitle">Browse the current approved controlled documents available for your role or assignment.</div>
          </div>
          <div class="cvn-library-summary"><strong>${records.length}</strong><span>APPROVED DOCUMENTS</span></div>
        </div>
        <div class="cvn-library-category-grid">
          ${categoryCard({key:'policies',label:'GOVERNANCE',title:'Policies',description:'Company governance, workforce, safety, privacy, and compliance requirements.',count:groups.policies.length})}
          ${categoryCard({key:'sops',label:'OPERATIONS',title:'Standard Operating Procedures',description:'Field procedures, route execution, safety response, and clinical logistics controls.',count:groups.sops.length})}
          ${categoryCard({key:'bbp',label:'SAFETY & EXPOSURE',title:'BBP & Occupational Exposure',description:'Exposure-control policy, ECP, vaccination, incident response, training, and records.',count:groups.bbp.length})}
          ${categoryCard({key:'other',label:'CONTROLLED KNOWLEDGE',title:'Other Controlled Documents',description:'Additional controlled plans, standards, references, and approved documents.',count:groups.other.length})}
        </div>
        <div class="cvn-library-category-view" data-current-library-category-view hidden></div>
      </div>
    </div>`;
}

function openCurrentLibraryCategory(category){
  const library = document.querySelector('#corevian-university-app [data-current-library]');
  if(!library) return;
  const view = library.querySelector('[data-current-library-category-view]');
  if(!view) return;
  const currentVersionByDocument = new Map();
  docState.versions.forEach(v=>{
    if(!v.document_id) return;
    const existing = currentVersionByDocument.get(v.document_id);
    if(!existing){ currentVersionByDocument.set(v.document_id,v); return; }
    const ed = existing.effective_date ? new Date(existing.effective_date).getTime() : 0;
    const cd = v.effective_date        ? new Date(v.effective_date).getTime()        : 0;
    if(cd>ed){ currentVersionByDocument.set(v.document_id,v); return; }
    if(cd===ed && String(v.version_number||'').localeCompare(String(existing.version_number||''),undefined,{numeric:true,sensitivity:'base'})>0){
      currentVersionByDocument.set(v.document_id,v);
    }
  });
  const records = Array.from(currentVersionByDocument.values()).map(v=>{
    const d = documentLabel(v);
    if(!d) return null;
    const roles = docState.roleAssignments.filter(r=>String(r.document_version_id)===String(v.id)).map(r=>r.role);
    const code = String(d.code||'').toUpperCase();
    let cat = 'other';
    if(code.startsWith('CHL-POL-'))       cat = code==='CHL-POL-015'?'bbp':'policies';
    else if(code.startsWith('CHL-SOP-')) cat = 'sops';
    else if(code.startsWith('CHL-BBP-')||code.startsWith('CHL-ECP-')) cat = 'bbp';
    return {v,d,roles,code,category:cat};
  }).filter(Boolean).filter(r=>r.category===category)
    .sort((a,b)=>a.code.localeCompare(b.code,undefined,{numeric:true,sensitivity:'base'}));
  const categoryInfo = {
    policies:{ eyebrow:'GOVERNANCE & COMPLIANCE',title:'Policies',description:'Current approved Corevian policies available for your role or assignment.' },
    sops:    { eyebrow:'OPERATIONS',title:'Standard Operating Procedures',description:'Current approved operational procedures available for your role or assignment.' },
    bbp:     { eyebrow:'SAFETY & EXPOSURE',title:'BBP & Occupational Exposure',description:'Current approved Bloodborne Pathogens and occupational-exposure program documents.' },
    other:   { eyebrow:'CONTROLLED KNOWLEDGE',title:'Other Controlled Documents',description:'Additional current approved controlled material available for your role or assignment.' }
  };
  const info = categoryInfo[category]||categoryInfo.other;
  const aMap = assignmentMap();
  const acks = ackMap();
  const cards = records.length
    ? records.map(({v,d,roles})=>{
        const assignment = aMap.get(v.id)||aMap.get(String(v.id));
        const ack = acks.get(v.id)||acks.get(String(v.id));
        const ackRequired = !!v.acknowledgment_required;
        let statusLabel = 'Approved';
        let statusCls   = 'complete';
        if(ackRequired && !ack && assignment){
          statusLabel = 'Acknowledgment Required';
          statusCls   = 'pending';
        } else if(ack){
          statusLabel = 'Acknowledged';
        }
        return `
          <div class="cvn-doc-card">
            <div class="cvn-doc-card-head">
              <div>
                <div class="cvn-doc-code">${cvnEsc(d.code)}${v.version_number?` · VERSION ${cvnEsc(v.version_number)}`:''}</div>
                <div class="cvn-doc-name">${cvnEsc(d.title)}</div>
                <div class="cvn-doc-meta">Effective ${cvnDate(v.effective_date)}${roles.length?` · Applies to: ${cvnEsc(roles.join(', '))}`:''}</div>
              </div>
              <span class="cvn-doc-status ${statusCls}">${statusLabel}</span>
            </div>
            ${d.description?`<div class="cvn-doc-desc">${cvnEsc(d.description)}</div>`:''}
            ${flags(v)}
            <div class="cvn-doc-actions">
              <button class="cvn-doc-btn" type="button" data-open-version="${cvnEsc(v.id)}">
                ${ackRequired && !ack ? 'Read & Acknowledge' : 'Read Current Version'}
              </button>
            </div>
          </div>`;
      }).join('')
    : `<div class="cvn-doc-empty">No approved documents are currently available in this category.</div>`;
  view.innerHTML = `
    <div class="cvn-library-category-detail">
      <button type="button" class="cvn-library-back" data-current-library-back>← All Current Documents</button>
      <div class="cvn-library-detail-head">
        <div class="cvn-library-eyebrow">${cvnEsc(info.eyebrow)}</div>
        <h2 class="cvn-library-title">${cvnEsc(info.title)}</h2>
        <div class="cvn-library-subtitle">${cvnEsc(info.description)}</div>
        <div class="cvn-library-detail-count">${records.length} ${records.length===1?'DOCUMENT':'DOCUMENTS'}</div>
      </div>
      <div class="cvn-doc-list">${cards}</div>
    </div>`;
  const heading = library.querySelector('.cvn-library-heading');
  const grid    = library.querySelector('.cvn-library-category-grid');
  if(heading) heading.hidden = true;
  if(grid)    grid.hidden    = true;
  view.hidden = false;
  const back = view.querySelector('[data-current-library-back]');
  if(back){
    back.addEventListener('click',()=>{
      view.hidden = true;
      if(heading) heading.hidden = false;
      if(grid)    grid.hidden    = false;
    });
  }
  view.querySelectorAll('[data-open-version]').forEach(btn=>{
    btn.addEventListener('click',async function(){
      docState.selectedVersionId = this.getAttribute('data-open-version');
      await renderPanel();
      const content = document.querySelector('#corevian-university-app .content');
      if(content) content.scrollTo({top:0,behavior:'smooth'});
    });
  });
}

/* ── MY ACKNOWLEDGMENTS HISTORY ────────────────────────────── */

function historyHtml(){
  if(!docState.acknowledgments.length){
    return `<div class="cvn-doc-empty">No controlled-document acknowledgments have been recorded for your account yet.</div>`;
  }
  const versions = versionMap();
  return `<div class="cvn-doc-list">${docState.acknowledgments.map(a=>{
    const v = versions.get(a.document_version_id)||versions.get(String(a.document_version_id));
    const d = v ? documentLabel(v) : {code:'CONTROLLED DOCUMENT',title:'Historical acknowledgment'};
    return `
      <div class="cvn-doc-card">
        <div class="cvn-doc-card-head">
          <div>
            <div class="cvn-doc-code">${cvnEsc(d.code)}${v?' · VERSION '+cvnEsc(v.version_number):''}</div>
            <div class="cvn-doc-name">${cvnEsc(d.title)}</div>
            <div class="cvn-doc-meta">Acknowledged ${cvnDate(a.acknowledged_at,true)}</div>
          </div>
          <span class="cvn-doc-status complete">Acknowledged</span>
        </div>
        <div class="cvn-doc-desc">${cvnEsc(a.acknowledgment_statement)}</div>
      </div>`;
  }).join('')}</div>`;
}

/* ── LEADERSHIP DOCUMENT READINESS ─────────────────────────── */

async function readinessHtml(){
  const data     = await loadLeadershipReadiness();
  const profiles = new Map(data.profiles.map(p=>[p.user_id,p]));
  const versions = versionMap();
  const ackKeys  = new Set(data.acks.map(a=>a.user_id+'|'+String(a.document_version_id)));
  const total     = data.assignments.length;
  const completed = data.assignments.filter(a=>a.completed_at||a.status==='completed'||ackKeys.has(a.user_id+'|'+String(a.document_version_id))).length;
  const overdue   = data.assignments.filter(a=>effectiveAssignmentStatus(a)==='overdue').length;
  const rows = data.assignments.map(a=>{
    const v = versions.get(a.document_version_id)||versions.get(String(a.document_version_id));
    if(!v) return '';
    const d = documentLabel(v);
    const p = profiles.get(a.user_id);
    const acknowledged = ackKeys.has(a.user_id+'|'+String(a.document_version_id));
    const status = acknowledged ? 'completed' : effectiveAssignmentStatus(a);
    return `
      <div class="cvn-doc-card">
        <div class="cvn-doc-card-head">
          <div>
            <div class="cvn-doc-code">${cvnEsc(d.code)} · VERSION ${cvnEsc(v.version_number)}</div>
            <div class="cvn-doc-name">${cvnEsc(p?p.full_name:'Assigned workforce member')}</div>
            <div class="cvn-doc-meta">${cvnEsc(p&&p.job_title?p.job_title:(p?p.role:''))} · Due ${cvnDate(a.due_at)}</div>
          </div>
          <span class="cvn-doc-status ${statusClass(status)}">${cvnEsc(status.replaceAll('_',' '))}</span>
        </div>
        <div class="cvn-doc-desc">${cvnEsc(d.title)}</div>
      </div>`;
  }).join('');
  return `
    <div class="cvn-doc-readiness-grid">
      <div class="cvn-doc-readiness-card"><strong>${total}</strong><span>Workforce Assignments</span></div>
      <div class="cvn-doc-readiness-card"><strong>${completed}</strong><span>Completed / Acknowledged</span></div>
      <div class="cvn-doc-readiness-card"><strong>${overdue}</strong><span>Overdue</span></div>
    </div>
    ${rows?`<div class="cvn-doc-list">${rows}</div>`:`<div class="cvn-doc-empty">No workforce document assignments are currently available.</div>`}`;
}

/* ── DOCUMENT DETAIL VIEW ──────────────────────────────────────
   
   THE ACK BOX FIX — why it wasn't showing:
   
   1. UUID type mismatch: assignmentMap().get(v.id) was returning
      undefined when v.id is a UUID object and the map key was a
      string (or vice versa). Now both raw and String() are indexed.
   
   2. Library tab had no assignment context: documents opened from
      Current Documents showed no ack box because the library card
      didn't know whether the user had an assignment. Now we look up
      the assignment at render time using both key types.
   
   3. Statement mismatch: the checkbox text and the RPC payload now
      use ACK_STATEMENT — a single constant defined at the top.
      The employee reads exactly what gets recorded.
   ─────────────────────────────────────────────────────────────── */

function detailHtml(versionId){
  const v = versionMap().get(versionId)||versionMap().get(String(versionId));
  if(!v) return `<div class="cvn-doc-error">The selected controlled document version is not available.</div>`;
  const d          = documentLabel(v);
  const aMap       = assignmentMap();
  const acks       = ackMap();
  /* Look up with both raw id and string to survive type differences */
  const assignment = aMap.get(v.id) || aMap.get(String(v.id));
  const ack        = acks.get(v.id) || acks.get(String(v.id));

  const body = v.content_text
    ? cvnEsc(v.content_text).replace(/\n/g,'<br>')
    : '<em style="opacity:.6">The controlled text for this version is not stored in the portal. Contact Operations or Document Control for the approved source document.</em>';

  const ackRequired   = !!v.acknowledgment_required;
  const hasAssignment = !!assignment;
  const canAck        = ackRequired && hasAssignment && !ack;
  const needsAssignment = ackRequired && !hasAssignment && !ack;

  /* ── Acknowledgment block — shows on ALL document types (policies,
        SOPs, BBP) when ackRequired and assignment exists and not yet done */
  const ackBlock = canAck ? `
    <div class="cvn-doc-ackbox" id="cvnAckBox">
      <div class="cvn-doc-ack-header">
        <strong>Document Acknowledgment Required</strong>
        <p style="margin:8px 0 0;font-size:13px;opacity:.8;line-height:1.6;">
          Read the complete document above before recording your acknowledgment.
          This record is version-locked to
          <strong>${cvnEsc(d.code)} Version ${cvnEsc(v.version_number)}</strong>
          and cannot be removed once submitted.
        </p>
      </div>
      <label class="cvn-doc-checkline">
        <input id="cvnDocAckCheck" type="checkbox">
        <span>${cvnEsc(ACK_STATEMENT)}</span>
      </label>
      <div class="cvn-doc-ack-actions">
        <button
          class="cvn-doc-btn primary"
          id="cvnDocAckBtn"
          type="button"
          data-ack-assignment="${cvnEsc(assignment.id)}"
          data-ack-version="${cvnEsc(String(v.id))}"
          data-ack-code="${cvnEsc(d.code)}"
          data-ack-version-number="${cvnEsc(v.version_number)}"
        >
          Record Acknowledgment
        </button>
      </div>
    </div>` : '';

  /* Optimistic placeholder — hidden until ack succeeds in background */
  const ackOptimisticPlaceholder = canAck ? `
    <div id="cvnAckDonePlaceholder" style="display:none;margin-top:18px;
      border-left:3px solid #16a34a;padding-left:14px;">
      <strong style="color:#4ADE80;">ACKNOWLEDGED</strong><br>
      ${cvnEsc(d.code)} · Version ${cvnEsc(v.version_number)}<br>
      <span id="cvnAckDoneTimestamp" style="font-size:13px;opacity:.8;"></span><br>
      <span style="font-size:12px;opacity:.6;">This acknowledgment is permanently recorded and cannot be modified.</span>
    </div>` : '';

  const ackDoneBlock = ack ? `
    <div class="cvn-doc-notice" style="margin-top:18px;border-left:3px solid #16a34a;padding-left:14px;">
      <strong style="color:#4ADE80;">ACKNOWLEDGED</strong><br>
      ${cvnEsc(d.code)} · Version ${cvnEsc(v.version_number)}<br>
      ${cvnDate(ack.acknowledged_at,true)}<br>
      <span style="font-size:12px;opacity:.6;">This acknowledgment is permanently recorded and cannot be modified.</span>
    </div>` : '';

  const needsAssignmentBlock = needsAssignment ? `
    <div class="cvn-doc-notice" style="margin-top:18px;" data-mark-read-anchor>
      This document version requires acknowledgment but is not currently
      assigned to your account. Contact your supervisor or Operations
      to receive a formal assignment before your acknowledgment can be recorded.
    </div>` : '';

  const noAckRequiredBlock = (!ackRequired && assignment) ? `
    <div class="cvn-doc-notice" style="margin-top:18px;" data-mark-read-anchor>
      This assignment does not require a formal acknowledgment.
      Additional training or competency requirements may apply — check your
      Qualification Passport for the complete requirement status.
    </div>` : '';

  return `
    <div class="cvn-doc-detail" id="cvnDocumentTop">
      <div class="cvn-doc-detail-top">
        <div>
          <div class="cvn-doc-code">${cvnEsc(d.code)} · VERSION ${cvnEsc(v.version_number)}</div>
          <div class="cvn-doc-name">${cvnEsc(d.title)}</div>
          <div class="cvn-doc-meta">
            Status: Approved &nbsp;·&nbsp;
            Effective ${cvnDate(v.effective_date)} &nbsp;·&nbsp;
            Review due ${cvnDate(v.review_due_date)}
          </div>
        </div>
        <button class="cvn-doc-btn" type="button" data-close-document>
          Back to ${docState.activeTab==='library'?'Current Documents':'Required Reading'}
        </button>
      </div>

      ${v.change_summary?`
        <div class="cvn-doc-notice" style="margin-bottom:16px;">
          <strong style="color:var(--white,#fff);">Version summary:</strong> ${cvnEsc(v.change_summary)}
        </div>`:''}

      <div class="cvn-doc-body">${body}</div>

      ${ackBlock}
      ${ackOptimisticPlaceholder}
      ${ackDoneBlock}
      ${needsAssignmentBlock}
      ${noAckRequiredBlock}

      <div class="cvn-doc-bottom-nav">
        <button class="cvn-doc-btn" type="button"
          onclick="document.getElementById('cvnDocumentTop')?.scrollIntoView({behavior:'smooth',block:'start'})">
          ↑ Back to Top
        </button>
        <button class="cvn-doc-btn" type="button" data-close-document>
          Back to ${docState.activeTab==='library'?'Current Documents':'Required Reading'}
        </button>
      </div>
    </div>`;
}

/* ── QUALIFICATION PASSPORT ────────────────────────────────────
   
   Shows all six evidence types independently per the Corevian
   qualification framework:
     1. Document Acknowledgment
     2. Training Completion
     3. Assessment Score / Certification
     4. Competency Verification
     5. Field Readiness
     6. Operational Authorization
   
   Evidence types 3-6 are read from Supabase tables if they exist.
   Graceful degradation if a table is not yet provisioned.
   ─────────────────────────────────────────────────────────────── */

async function renderQualificationPassport(){
  if(isDemoVisible()){
    const root = document.getElementById('contentInner');
    if(root) root.innerHTML = `<div class="cvn-doc-error">The Qualification Passport is unavailable in Executive Demo mode.</div>`;
    return;
  }
  const root = document.getElementById('contentInner');
  if(!root) return;
  root.innerHTML = `<div class="cvn-docs-shell"><div class="cvn-doc-notice">Loading your Qualification Passport…</div></div>`;
  try{
    await loadIdentity();
    await loadDocumentData();
    const client = getClient();
    const uid    = docState.user.id;
    const profile = docState.profile;

    /* ── Evidence type 1: Document Acknowledgments ── */
    const acks     = docState.acknowledgments;
    const versions = versionMap();

    /* ── Evidence types 3-6: read from DB with graceful fallback ── */
    let certifications = [], competencies = [], endorsements = [], authorizations = [];
    try{
      const certRes = await client.from('certifications')
        .select('id,cert_type,curriculum_version,score,issued_at,expires_at,issued_by')
        .eq('user_id',uid).order('issued_at',{ascending:false});
      if(!certRes.error) certifications = certRes.data||[];
    }catch(e){ console.warn('[Passport] certifications table not yet available'); }
    try{
      const compRes = await client.from('competency_records')
        .select('id,competency_id,verified_by,verified_at,notes,status')
        .eq('user_id',uid).order('verified_at',{ascending:false});
      if(!compRes.error) competencies = compRes.data||[];
    }catch(e){ console.warn('[Passport] competency_records table not yet available'); }
    try{
      const endRes = await client.from('endorsements')
        .select('id,endorsement_type,authorized_by,authorized_at,expires_at,status')
        .eq('user_id',uid).order('authorized_at',{ascending:false});
      if(!endRes.error) endorsements = endRes.data||[];
    }catch(e){ console.warn('[Passport] endorsements table not yet available'); }

    /* ── Training completion from unit_progress if available ── */
    let unitProgress = [];
    try{
      const upRes = await client.from('unit_progress')
        .select('unit_id,status,best_score,completed_at,curriculum_version')
        .eq('user_id',uid).eq('status','passed');
      if(!upRes.error) unitProgress = upRes.data||[];
    }catch(e){ console.warn('[Passport] unit_progress table not yet available'); }

    /* ── Render ── */
    const evidenceBlock = (eyebrow, title, status, statusCls, content) => `
      <div class="cvn-passport-block">
        <div class="cvn-passport-block-head">
          <div>
            <div class="cvn-passport-eyebrow">${cvnEsc(eyebrow)}</div>
            <div class="cvn-passport-title">${cvnEsc(title)}</div>
          </div>
          <span class="cvn-doc-status ${statusCls}">${cvnEsc(status)}</span>
        </div>
        <div class="cvn-passport-content">${content}</div>
      </div>`;

    /* 1. Document Acknowledgments */
    const ackContent = acks.length
      ? `<div class="cvn-passport-list">${acks.map(a=>{
          const v = versions.get(a.document_version_id)||versions.get(String(a.document_version_id));
          const d = v ? documentLabel(v) : {code:'CONTROLLED DOCUMENT',title:''};
          return `<div class="cvn-passport-row">
            <div class="cvn-passport-row-label">${cvnEsc(d.code)}${v?` · v${cvnEsc(v.version_number)}`:''}</div>
            <div class="cvn-passport-row-value">${cvnDate(a.acknowledged_at,true)}</div>
          </div>`;
        }).join('')}</div>`
      : `<p class="cvn-passport-empty">No document acknowledgments recorded yet. Complete your required reading in Policies &amp; SOPs.</p>`;
    const ackStatus = acks.length>0 ? 'Acknowledged' : 'Pending';
    const ackCls    = acks.length>0 ? 'complete' : 'pending';

    /* 2. Training Completion */
    const trainingContent = unitProgress.length
      ? `<div class="cvn-passport-list">${unitProgress.map(u=>`
          <div class="cvn-passport-row">
            <div class="cvn-passport-row-label">Unit ${cvnEsc(String(u.unit_id))} · ${cvnEsc(u.curriculum_version||'')}</div>
            <div class="cvn-passport-row-value">${u.best_score!=null?Math.round(u.best_score*100)+'%':''} · ${cvnDate(u.completed_at)}</div>
          </div>`).join('')}</div>`
      : `<p class="cvn-passport-empty">Training completion records will appear here once your Corevian University progress is connected to the production database.</p>`;
    const trainingStatus = unitProgress.length>0 ? `${unitProgress.length} Units Complete` : 'Not Started';
    const trainingCls    = unitProgress.length>0 ? 'complete' : 'pending';

    /* 3. Assessment Certifications */
    const certContent = certifications.length
      ? `<div class="cvn-passport-list">${certifications.map(c=>`
          <div class="cvn-passport-row">
            <div class="cvn-passport-row-label">${cvnEsc(c.cert_type)} · ${cvnEsc(c.curriculum_version||'')}</div>
            <div class="cvn-passport-row-value">${c.score!=null?Math.round(c.score*100)+'%':''} · ${cvnDate(c.issued_at)} ${c.expires_at?`· Expires ${cvnDate(c.expires_at)}`:''}</div>
          </div>`).join('')}</div>`
      : `<p class="cvn-passport-empty">Assessment certification records will appear here once the assessment database is connected.</p>`;
    const certStatus = certifications.length>0 ? 'Certified' : 'Not Yet Certified';
    const certCls    = certifications.length>0 ? 'complete' : 'pending';

    /* 4. Competency Verification */
    const compContent = competencies.length
      ? `<div class="cvn-passport-list">${competencies.map(c=>`
          <div class="cvn-passport-row">
            <div class="cvn-passport-row-label">${cvnEsc(c.competency_id)} · ${cvnEsc(c.status||'')}</div>
            <div class="cvn-passport-row-value">Verified by ${cvnEsc(c.verified_by||'evaluator')} · ${cvnDate(c.verified_at)}</div>
          </div>`).join('')}</div>`
      : `<p class="cvn-passport-empty">Practical competency verifications are recorded by your trainer or supervisor after hands-on assessment. No records on file yet.</p>`;
    const compStatus = competencies.filter(c=>c.status==='competent').length>0 ? 'Competent' : 'Not Yet Verified';
    const compCls    = competencies.filter(c=>c.status==='competent').length>0 ? 'complete' : 'pending';

    /* 5. Field Readiness / Service Endorsements */
    const activeEndorsements = endorsements.filter(e=>e.status==='active');
    const endContent = endorsements.length
      ? `<div class="cvn-passport-list">${endorsements.map(e=>`
          <div class="cvn-passport-row">
            <div class="cvn-passport-row-label">${cvnEsc(e.endorsement_type)}</div>
            <div class="cvn-passport-row-value">
              <span class="cvn-doc-status ${e.status==='active'?'complete':e.status==='suspended'?'overdue':'pending'}">${cvnEsc(e.status||'pending')}</span>
              &nbsp; Authorized ${cvnDate(e.authorized_at)} by ${cvnEsc(e.authorized_by||'Operations')}
              ${e.expires_at?`· Expires ${cvnDate(e.expires_at)}`:''}
            </div>
          </div>`).join('')}</div>`
      : `<p class="cvn-passport-empty">Service endorsements are granted by Operations after written certification and practical competency are verified. No endorsements on file yet.</p>`;
    const endStatus = activeEndorsements.length>0 ? `${activeEndorsements.length} Active` : 'None Active';
    const endCls    = activeEndorsements.length>0 ? 'complete' : 'pending';

    /* 6. Operational Authorization */
    const authContent = activeEndorsements.length>0
      ? `<div class="cvn-passport-auth-notice complete">
          <strong style="color:#4ADE80;">AUTHORIZED FOR INDEPENDENT OPERATION</strong><br>
          <span style="font-size:13px;opacity:.8;">
            ${profile.full_name} holds ${activeEndorsements.length} active service endorsement${activeEndorsements.length!==1?'s':''}.
            Authorization is role-specific and endorsement-specific.
            See Field Readiness above for the complete endorsement record.
          </span>
        </div>`
      : `<div class="cvn-passport-auth-notice pending">
          <strong>NOT YET AUTHORIZED FOR INDEPENDENT OPERATION</strong><br>
          <span style="font-size:13px;opacity:.8;">
            Operational authorization requires: written certification (90%), practical
            competency verification by an evaluator, and service endorsement granted
            by Operations. Complete the steps above to progress toward authorization.
          </span>
        </div>`;
    const authStatus = activeEndorsements.length>0 ? 'Authorized' : 'Not Yet Authorized';
    const authCls    = activeEndorsements.length>0 ? 'complete' : 'pending';

    root.innerHTML = `
      <div class="cvn-docs-shell">
        <div class="cvn-docs-hero">
          <div class="cvn-docs-eyebrow">Qualification Passport · ${cvnEsc(profile.full_name)}</div>
          <h1 class="cvn-docs-title">Your complete qualification record.</h1>
          <div class="cvn-docs-subtitle">
            Each evidence type is recorded and maintained independently.
            Acknowledging a document is not the same as completing training.
            Completing training is not the same as demonstrating competency.
            Demonstrating competency is not the same as receiving operational authorization.
          </div>
        </div>

        <div class="cvn-passport-grid">
          ${evidenceBlock('EVIDENCE TYPE 1','Document Acknowledgment',ackStatus,ackCls,ackContent)}
          ${evidenceBlock('EVIDENCE TYPE 2','Training Completion',trainingStatus,trainingCls,trainingContent)}
          ${evidenceBlock('EVIDENCE TYPE 3','Assessment Certification',certStatus,certCls,certContent)}
          ${evidenceBlock('EVIDENCE TYPE 4','Competency Verification',compStatus,compCls,compContent)}
          ${evidenceBlock('EVIDENCE TYPE 5','Field Readiness & Service Endorsements',endStatus,endCls,endContent)}
          ${evidenceBlock('EVIDENCE TYPE 6','Operational Authorization',authStatus,authCls,authContent)}
        </div>
      </div>`;

  }catch(err){
    console.error('[Corevian Passport]', err);
    root.innerHTML = `<div class="cvn-docs-shell"><div class="cvn-doc-error">${cvnEsc(err.message||'Unable to load the Qualification Passport.')}</div></div>`;
  }
}

window.renderQualificationPassport = renderQualificationPassport;

/* ── RENDER PANEL ──────────────────────────────────────────── */

async function renderPanel(){
  const panel = document.getElementById('cvnDocsPanel');
  if(!panel) return;
  if(docState.selectedVersionId){
    panel.innerHTML = detailHtml(docState.selectedVersionId);
    bindPanelActions();
    return;
  }
  if(docState.activeTab==='required')       panel.innerHTML = requiredHtml();
  else if(docState.activeTab==='library')   panel.innerHTML = libraryHtml();
  else if(docState.activeTab==='history')   panel.innerHTML = historyHtml();
  else if(docState.activeTab==='readiness'){
    panel.innerHTML = `<div class="cvn-doc-notice">Loading workforce document readiness…</div>`;
    try{ panel.innerHTML = await readinessHtml(); }
    catch(err){ panel.innerHTML = `<div class="cvn-doc-error">${cvnEsc(err.message||'Unable to load document readiness.')}</div>`; }
  }
  bindPanelActions();
}

/* ── TAB BINDING ───────────────────────────────────────────── */

function bindTabActions(){
  document.querySelectorAll('[data-doc-tab]').forEach(btn=>{
    btn.addEventListener('click', async function(){
      docState.activeTab = this.getAttribute('data-doc-tab')||'required';
      docState.selectedVersionId = null;
      document.querySelectorAll('[data-doc-tab]').forEach(x=>x.classList.toggle('active',x===this));
      await renderPanel();
    });
  });
}

/* ── PANEL ACTION BINDING ──────────────────────────────────── */

function bindPanelActions(){
  const currentLibrary = document.querySelector('#corevian-university-app [data-current-library]');
  if(currentLibrary){
    currentLibrary.querySelectorAll('[data-current-library-category]').forEach(button=>{
      button.addEventListener('click', function(){
        const category = this.getAttribute('data-current-library-category');
        if(category) openCurrentLibraryCategory(category);
      });
    });
  }

  const requiredLibrary = document.querySelector('#corevian-university-app .cvn-required-library');
  if(requiredLibrary){
    const home            = requiredLibrary.querySelector('[data-library-home]');
    const sections        = Array.from(requiredLibrary.querySelectorAll('[data-library-section]'));
    const categoryButtons = Array.from(requiredLibrary.querySelectorAll('[data-doc-category]'));
    const backButtons     = Array.from(requiredLibrary.querySelectorAll('[data-library-back]'));
    const searchInput     = requiredLibrary.querySelector('#cvnRequiredSearch');
    const filterButtons   = Array.from(requiredLibrary.querySelectorAll('[data-required-filter]'));
    let activeCategory = null;
    let activeFilter   = 'all';

    function clearLibraryHiding(){
      requiredLibrary.querySelectorAll('.cvn-library-hidden').forEach(el=>el.classList.remove('cvn-library-hidden'));
    }
    function applyLibraryFilters(){
      const query = String(searchInput?searchInput.value:'').trim().toLowerCase();
      const visibleSection = activeCategory ? requiredLibrary.querySelector(`[data-library-section="${activeCategory}"]`) : null;
      const scope = visibleSection||requiredLibrary;
      const rows  = Array.from(scope.querySelectorAll('.cvn-library-doc-row'));
      rows.forEach(row=>{
        const searchText  = String(row.textContent||'').toLowerCase();
        const status      = String(row.getAttribute('data-document-status')||'').toLowerCase();
        const matchSearch = !query||searchText.includes(query);
        let matchFilter   = true;
        if(activeFilter==='action')  matchFilter = status!=='completed'&&status!=='acknowledged'&&status!=='waived';
        if(activeFilter==='overdue') matchFilter = status==='overdue';
        row.classList.toggle('cvn-library-hidden',!(matchSearch&&matchFilter));
      });
      scope.querySelectorAll('.cvn-bbp-group').forEach(group=>{
        const groupRows = Array.from(group.querySelectorAll('.cvn-library-doc-row'));
        if(!groupRows.length) return;
        group.classList.toggle('cvn-library-hidden',!groupRows.some(r=>!r.classList.contains('cvn-library-hidden')));
      });
      const empty = requiredLibrary.querySelector('#cvnRequiredSearchEmpty');
      if(empty) empty.hidden = !(rows.length>0&&rows.every(r=>r.classList.contains('cvn-library-hidden')));
    }
    function openLibraryCategory(category){
      activeCategory = category;
      if(home) home.hidden = true;
      sections.forEach(s=>{ s.hidden = s.getAttribute('data-library-section')!==category; });
      clearLibraryHiding();
      applyLibraryFilters();
      const content = document.querySelector('#corevian-university-app .content');
      if(content) content.scrollTo({top:0,behavior:'smooth'});
    }
    function returnToLibraryHome(){
      activeCategory = null;
      if(home) home.hidden = false;
      sections.forEach(s=>{ s.hidden = true; });
      clearLibraryHiding();
      const content = document.querySelector('#corevian-university-app .content');
      if(content) content.scrollTo({top:0,behavior:'smooth'});
    }
    categoryButtons.forEach(b=>b.addEventListener('click',function(){
      const c=this.getAttribute('data-doc-category'); if(c) openLibraryCategory(c);
    }));
    backButtons.forEach(b=>b.addEventListener('click',returnToLibraryHome));
    if(searchInput){
      searchInput.addEventListener('input',function(){
        const query = String(this.value||'').trim().toLowerCase();
        if(query&&!activeCategory){
          const matchingRow = Array.from(requiredLibrary.querySelectorAll('.cvn-library-doc-row'))
            .find(row=>String(row.textContent||'').toLowerCase().includes(query));
          if(matchingRow){
            const cat = matchingRow.closest('[data-library-section]')?.getAttribute('data-library-section');
            if(cat){ openLibraryCategory(cat); return; }
          }
        }
        applyLibraryFilters();
      });
    }
    filterButtons.forEach(b=>b.addEventListener('click',function(){
      activeFilter = this.getAttribute('data-required-filter')||'all';
      filterButtons.forEach(i=>i.classList.toggle('active',i===this));
      applyLibraryFilters();
    }));
  }

  document.querySelectorAll('[data-open-version]').forEach(btn=>{
    btn.addEventListener('click',async function(){
      docState.selectedVersionId = this.getAttribute('data-open-version');
      await renderPanel();
      const content = document.querySelector('#corevian-university-app .content');
      if(content) content.scrollTo({top:0,behavior:'smooth'});
    });
  });
  document.querySelectorAll('[data-close-document]').forEach(btn=>{
    btn.addEventListener('click',async function(){
      docState.selectedVersionId = null;
      await renderPanel();
    });
  });
  document.querySelectorAll('[data-ack-assignment]').forEach(btn=>{
    btn.addEventListener('click',async function(){
      await acknowledgeAssignment(
        this.getAttribute('data-ack-assignment'),
        this,
        this.getAttribute('data-ack-version'),
        this.getAttribute('data-ack-code'),
        this.getAttribute('data-ack-version-number')
      );
    });
  });
}

/* ── ACKNOWLEDGMENT — OPTIMISTIC UI ────────────────────────────
   
   Optimistic update:
   1. Checkbox check is validated immediately
   2. DOM updates instantly — ack box hides, "ACKNOWLEDGED" appears
   3. Supabase RPC call runs in background
   4. On success: docState is refreshed silently
   5. On failure: DOM reverts, error shown, user can retry
   ─────────────────────────────────────────────────────────────── */

async function acknowledgeAssignment(assignmentId, button, versionId, docCode, versionNumber){
  const check = document.getElementById('cvnDocAckCheck');
  if(!check||!check.checked){
    alert('Please read the complete document and confirm the acknowledgment checkbox before recording.');
    return;
  }
  const client = getClient();
  if(!client){ alert('Supabase client is unavailable.'); return; }

  /* ── Optimistic DOM update — happens immediately ── */
  const ackBox         = document.getElementById('cvnAckBox');
  const placeholder    = document.getElementById('cvnAckDonePlaceholder');
  const timestampEl    = document.getElementById('cvnAckDoneTimestamp');
  const nowStr         = new Date().toLocaleString();

  if(ackBox)      ackBox.style.display = 'none';
  if(placeholder) placeholder.style.display = 'block';
  if(timestampEl) timestampEl.textContent = nowStr;

  try{
    const result = await client.rpc('record_document_acknowledgment',{
      p_assignment_id:            assignmentId,
      p_acknowledgment_statement: ACK_STATEMENT
    });
    if(result.error) throw result.error;

    /* ── Silent background refresh — updates docState without spinner ── */
    try{ await loadDocumentData(); }catch(e){ console.warn('[Corevian Docs] Background refresh failed:', e); }

    /* ── After success, if still on this document, refresh the panel ── */
    if(docState.selectedVersionId === versionId||docState.selectedVersionId===String(versionId)){
      docState.selectedVersionId = null;
      docState.activeTab = 'required';
      const tabs = document.getElementById('cvnDocsTabs');
      if(tabs) tabs.innerHTML = tabsHtml();
      bindTabActions();
      await renderPanel();
    }

  }catch(err){
    /* ── Revert optimistic update on failure ── */
    if(ackBox)      ackBox.style.display = '';
    if(placeholder) placeholder.style.display = 'none';
    button.disabled    = false;
    button.textContent = 'Record Acknowledgment';
    alert(err&&err.message ? err.message : 'Unable to record the document acknowledgment. Please try again.');
  }
}

/* ── RENDER CONTROLLED DOCUMENTS ──────────────────────────────*/

async function renderControlledDocuments(){
  if(isDemoVisible()){
    const root = document.getElementById('contentInner');
    if(root) root.innerHTML = `<div class="cvn-doc-error">Live controlled-document records are unavailable in Executive Demo mode.</div>`;
    return;
  }
  if(docState.loading) return;
  docState.loading = true;
  renderLoading();
  try{
    await loadIdentity();
    await loadDocumentData();
    const root = document.getElementById('contentInner');
    if(!root) return;
    root.innerHTML = `
      <div class="cvn-docs-shell">
        <div class="cvn-docs-hero">
          <div class="cvn-docs-eyebrow">Controlled Knowledge · Policies & SOPs</div>
          <h1 class="cvn-docs-title">Current procedures. Exact versions. Documented acknowledgment.</h1>
          <div class="cvn-docs-subtitle">
            Access approved Corevian controlled documents assigned to your role or account.
            Required-reading records are tied to the exact document version reviewed.
            Historical acknowledgments remain part of the workforce record when a later version is issued.
          </div>
        </div>
        ${renderStats()}
        <div id="cvnDocsTabs">${tabsHtml()}</div>
        <div class="cvn-doc-panel" id="cvnDocsPanel"></div>
      </div>`;
    bindTabActions();
    await renderPanel();
  }catch(err){
    console.error('Corevian controlled documents module:', err);
    renderError(err);
  }finally{
    docState.loading = false;
  }
}

window.renderControlledDocuments = renderControlledDocuments;

/* ── INIT ──────────────────────────────────────────────────── */

function init(){
  injectNav();
  installDemoObserver();
  installViewHook();
  if(!window.__corevianDocsRetry){
    window.__corevianDocsRetry = setInterval(function(){
      injectNav();
      installDemoObserver();
      installViewHook();
      if(document.getElementById('navDocuments')&&window.__corevianDocsViewHookReady){
        clearInterval(window.__corevianDocsRetry);
      }
    }, 400);
  }
}

if(document.readyState==='loading'){
  document.addEventListener('DOMContentLoaded',init);
}else{
  init();
}

})();
